/**
 * Supply placement.
 *
 * The bias here is the whole point: a level where the granola is lying in the
 * open on the way to the exit is a corridor with props in it. Scoring pushes
 * pickups deep and into corners, and a fixed share of them is reserved outright
 * for spots you have to go out of your way to reach.
 */

import { UNREACHABLE } from '../../core/grid';
import { weightedPick } from '../../core/rng';
import { Cell, type Grid, type ItemKind, type ItemSpot, type Region } from '../../core/types';
import { countWalkableNeighbors, type GenSite } from './site';

/** Metres of guaranteed nothing around the spawn point. */
const SUPPLY_EXCLUSION = 8;
/** Cells between two supply spots, so a cache never reads as a pile. */
const SUPPLY_SEPARATION = 3;
/** Share of the supplies placed in a closet or dead end rather than by score. */
const RESERVED_FOR_CACHES = 0.45;

/**
 * Where a guaranteed item goes, as a fraction of the level's deepest reachable
 * point: peak, and half-width of the band either side of it.
 *
 * Guaranteed items are scored to be *met*, not to be a reward. The ordinary
 * score pushes everything deep, which is right for the almond water and wrong
 * for the flashlight and the rules note — a tutorial the player reaches after
 * four hundred metres has already been overtaken by the thing it teaches. The
 * band starts past the opening rooms, so the first minutes are still empty
 * handed, and closes well inside the first stretch of the walk.
 */
const EARLY_PEAK = 0.22;
const EARLY_WIDTH = 0.18;

/**
 * Supplies. Scored, then drawn by weight rather than taken in order, so runs
 * differ: the good stuff tends to be deep, in a closet or at the end of
 * something, but never reliably enough to make looting mechanical.
 */
export function placeItems(
  site: GenSite,
  regionAt: Int32Array,
  walk: Int32Array,
  dist: Uint16Array,
  maxDist: number,
): ItemSpot[] {
  const { grid, rng, spec, regions } = site;
  const exclusion = SUPPLY_EXCLUSION / grid.cellSize;
  const score = new Float32Array(walk.length);
  const cache = new Float32Array(walk.length);
  const early = new Float32Array(walk.length);
  const lookup = new Int32Array(grid.cells.length).fill(-1);

  // A region you can only get into one way is the payoff for a wrong turn.
  const tucked = new Uint8Array(regions.length);
  const ring: boolean[] = [];
  for (let r = 0; r < regions.length; r++) {
    const region = regions[r]!;
    if (region.kind === 'closet') {
      tucked[r] = 1;
      continue;
    }
    if (region.w * region.h > 90) continue;
    tucked[r] = isDeadEndRegion(grid, region, ring) ? 1 : 0;
  }

  let total = 0;
  let cacheTotal = 0;
  let earlyTotal = 0;
  for (let k = 0; k < walk.length; k++) {
    const i = walk[k]!;
    lookup[i] = k;
    const d = dist[i]!;
    if (d === UNREACHABLE || d < exclusion) continue;
    const c = grid.cells[i]!;
    if (c !== Cell.Floor && c !== Cell.Damp) continue;

    const x = i % grid.w;
    const y = (i / grid.w) | 0;
    const open = countWalkableNeighbors(grid, x, y);
    let s = 0.3 + 1.7 * (d / maxDist);
    let tuckedHere = open <= 1;
    if (open <= 1) s += 1.8;
    else if (open === 2) s += 0.35;
    const rid = regionAt[i]!;
    if (rid >= 0) {
      const kind = regions[rid]!.kind;
      if (kind === 'closet') s += 1.6;
      else if (kind === 'warren') s += 0.9;
      else if (kind === 'hall') s -= 0.5;
      if (tucked[rid]) {
        s += 1.2;
        tuckedHere = true;
      }
    }
    score[k] = Math.max(0, s);
    total += score[k]!;
    if (tuckedHere) {
      cache[k] = score[k]!;
      cacheTotal += cache[k]!;
    }

    // The guaranteed pool, scored the other way round: a band a little way in,
    // out of the caches, and biased toward the corridors you walk down rather
    // than the rooms you might never enter.
    const band = 1 - Math.abs(d / maxDist - EARLY_PEAK) / EARLY_WIDTH;
    if (band > 0) {
      let e = band;
      if (tuckedHere) e *= 0.25;
      if (rid >= 0 && regions[rid]!.kind === 'hall') e *= 1.4;
      early[k] = e;
      earlyTotal += e;
    }
  }
  if (total <= 0) return [];

  // Most of the supplies are reserved for those tucked-away spots outright.
  // Scoring alone will not do it: dead ends are a tiny share of the floor area,
  // so a weighted draw over the whole level almost never lands in one.
  const reserved = Math.round(spec.supplyCount * RESERVED_FOR_CACHES);

  // Guaranteed items are drawn first and from the *scored* pool, never the
  // reserved cache pool: something the run cannot work without must not be
  // hidden in a closet the player may never open. Drawing them first also means
  // they survive the loop giving up early on a cramped level.
  const guaranteed = spec.guaranteedItems ?? [];

  const items: ItemSpot[] = [];
  for (let n = 0; n < spec.supplyCount; n++) {
    const forced = n < guaranteed.length;
    // A guaranteed item falls back to the ordinary scored pool if the early band
    // came up empty on a cramped level: "guaranteed" has to mean it.
    const fromEarly = forced && earlyTotal > 0;
    const fromCache = !forced && n - guaranteed.length < reserved && cacheTotal > 0;
    const pool = fromEarly ? early : fromCache ? cache : score;
    const poolTotal = fromEarly ? earlyTotal : fromCache ? cacheTotal : total;
    if (poolTotal <= 0) break;
    let r = rng.next() * poolTotal;
    let picked = -1;
    for (let k = 0; k < pool.length; k++) {
      const s = pool[k]!;
      if (s <= 0) continue;
      r -= s;
      if (r <= 0) {
        picked = k;
        break;
      }
    }
    if (picked < 0) break;

    const i = walk[picked]!;
    const x = i % grid.w;
    const y = (i / grid.w) | 0;
    const kind = forced ? guaranteed[n]! : weightedPick<ItemKind>(rng, spec.supplyTable);
    items.push({
      id: n,
      pos: grid.cellToWorld(x, y),
      kind,
      amount: kind === 'note' || kind === 'key' || kind === 'flashlight' ? 1 : rng.chance(0.22) ? 2 : 1,
    });

    // Clear the neighbourhood so supplies stay spread out.
    for (let dy = -SUPPLY_SEPARATION; dy <= SUPPLY_SEPARATION; dy++) {
      for (let dx = -SUPPLY_SEPARATION; dx <= SUPPLY_SEPARATION; dx++) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= grid.w || ny >= grid.h) continue;
        const k = lookup[ny * grid.w + nx]!;
        if (k < 0) continue;
        total -= score[k]!;
        cacheTotal -= cache[k]!;
        earlyTotal -= early[k]!;
        score[k] = 0;
        cache[k] = 0;
        early[k] = 0;
      }
    }
  }
  return items;
}

/**
 * True when the region has exactly one narrow way in. The run length matters:
 * a room whose whole wall was knocked out is open, not tucked away.
 */
function isDeadEndRegion(grid: Grid, r: Region, ring: boolean[]): boolean {
  ring.length = 0;
  const x0 = r.x - 1;
  const y0 = r.y - 1;
  const x1 = r.x + r.w;
  const y1 = r.y + r.h;
  for (let x = x0; x <= x1; x++) ring.push(grid.isWalkable(x, y0));
  for (let y = y0 + 1; y <= y1; y++) ring.push(grid.isWalkable(x1, y));
  for (let x = x1 - 1; x >= x0; x--) ring.push(grid.isWalkable(x, y1));
  for (let y = y1 - 1; y > y0; y--) ring.push(grid.isWalkable(x0, y));

  const n = ring.length;
  let runs = 0;
  let longest = 0;
  let current = 0;
  for (let i = 0; i < n; i++) {
    if (!ring[i]) {
      current = 0;
      continue;
    }
    if (!ring[(i - 1 + n) % n]) runs++;
    current++;
    if (current > longest) longest = current;
  }
  return runs === 1 && longest <= 3;
}
